import { Alert, Button, Card, Form, Input, Select, Space, Typography } from "antd";
import type { Claim } from "@zhihaoscm/entity";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { Grid as ZhGrid } from "zhihao-ui-react";

import request from "@/api";
import { DEFAULT_VALUE } from "@/config";
import { CLAIM_STATE } from "@/meta/logistics/deviceClaim";
import { useDeviceClaimActionAccess } from "./useDeviceClaimActionAccess";

export interface DeviceCandidate {
    id: number;
    name?: string;
    serialNo?: string;
}

export interface DeviceClaimFormPageProps {
    claimId?: number | string;
    loadClaim?: (id: number | string) => Promise<Partial<Claim> | undefined>;
    loadDevices?: (keyword?: string) => Promise<DeviceCandidate[]>;
    bindDevice?: (form: { id: number | string; deviceId: number; position: string }) => Promise<void>;
    permissions?: string[];
    banButton?: boolean;
}

/** 复用 Vue 设备认领绑定表单的详情接口。 */
export async function loadDeviceClaimForForm(id: number | string): Promise<Partial<Claim> | undefined> {
    const response = await request.service.get<Partial<Claim>>(`/claim/${id}`);
    return response.data;
}

/** 复用 Vue 设备名称/序列号候选接口。 */
export async function loadClaimDevices(keyword?: string): Promise<DeviceCandidate[]> {
    const response = await request.service.get<DeviceCandidate[]>("/device/selector", {
        params: keyword ? { keyword } : undefined
    });
    return response.data || [];
}

/** 复用 Vue `/claim/bind` 提交接口。 */
export async function bindClaimDevice(form: {
    id: number | string;
    deviceId: number;
    position: string;
}): Promise<void> {
    await request.service.post("/claim/bind", form);
}

/** 物流管理-设备认领绑定表单，覆盖 Vue 设备候选、必填校验和绑定 payload。 */
export function DeviceClaimFormPage({
    claimId,
    loadClaim = loadDeviceClaimForForm,
    loadDevices = loadClaimDevices,
    bindDevice = bindClaimDevice,
    permissions,
    banButton
}: DeviceClaimFormPageProps) {
    const params = useParams<{ id: string }>();
    const navigate = useNavigate();
    const resolvedId = claimId ?? params.id ?? 0;
    const [claim, setClaim] = useState<Partial<Claim>>();
    const [devices, setDevices] = useState<DeviceCandidate[]>([]);
    const [deviceId, setDeviceId] = useState<number>();
    const [position, setPosition] = useState("");
    const [deviceError, setDeviceError] = useState("");
    const [positionError, setPositionError] = useState("");
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    // 保留旧 Vue 表单提交按钮的 v-auth="'ROLE_DEVICE_BIND_W'" 语义，具体权限判断由共享 hook 承担。
    const { canBind, isButtonBanned } = useDeviceClaimActionAccess({ permissions, banButton });

    useEffect(() => {
        let disposed = false;
        void Promise.all([loadClaim(resolvedId), loadDevices()])
            .then(([claimResult, deviceResults]) => {
                if (disposed) return;
                setClaim(claimResult);
                setDevices(deviceResults);
                if (claimResult?.deviceId) setDeviceId(Number(claimResult.deviceId));
            })
            .catch(() => {
                if (!disposed) setErrorMessage("设备认领表单加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [loadClaim, loadDevices, resolvedId]);

    const searchDevices = (keyword: string) => {
        void loadDevices(keyword)
            .then(setDevices)
            .catch(() => setErrorMessage("设备候选加载失败"));
    };

    const submit = async () => {
        const nextDeviceError = deviceId ? "" : "请选择关联设备";
        const nextPositionError = position.trim() ? "" : "请输入安装位置";
        setDeviceError(nextDeviceError);
        setPositionError(nextPositionError);
        if (nextDeviceError || nextPositionError) return;
        const selectedDeviceId = deviceId;
        try {
            await bindDevice({ id: resolvedId, deviceId: selectedDeviceId as number, position: position.trim() });
            navigate(-1);
        } catch {
            setErrorMessage("设备绑定失败");
        }
    };

    return (
        <section aria-label="device-claim-form-page" className="flex min-h-full flex-col">
            <Card>
                <Typography.Title level={4}>设备认领处理</Typography.Title>
                {errorMessage ? <Alert className="mb-4" message={errorMessage} role="alert" type="error" /> : null}
                <Typography.Title level={5}>申领信息</Typography.Title>
                <Form layout="vertical">
                    <ZhGrid cols={4} gap={[30, 0]}>
                        <Form.Item label="承运商账号">
                            {claim?.customerInfo
                                ? `${claim.customerInfo.institutionName || claim.customerInfo.realName || DEFAULT_VALUE} | ${claim.customerInfo.mobile || DEFAULT_VALUE} | ${claim.customerInfo.code || DEFAULT_VALUE}`
                                : DEFAULT_VALUE}
                        </Form.Item>
                        <Form.Item label="船舶">
                            {claim?.shipId ? (
                                <Link to={`/logistics/shipManage/shipInfo/shipInfoDetail/${claim.shipId}`}>
                                    {claim.shipNameCn
                                        ? `${claim.shipNameCn} / ${claim.shipName || DEFAULT_VALUE}`
                                        : claim.shipName || DEFAULT_VALUE}
                                </Link>
                            ) : (
                                claim?.shipName || DEFAULT_VALUE
                            )}
                        </Form.Item>
                        <Form.Item label="MMSI">{claim?.shipId || DEFAULT_VALUE}</Form.Item>
                    </ZhGrid>
                </Form>
                <Typography.Title level={5}>设备绑定信息</Typography.Title>
                <Form layout="vertical" onFinish={() => void submit()}>
                    <ZhGrid cols={4} gap={[30, 0]}>
                        <Form.Item
                            label="关联设备"
                            validateStatus={deviceError ? "error" : undefined}
                            help={deviceError || undefined}
                        >
                            <Select
                                showSearch
                                allowClear
                                aria-label="关联设备"
                                filterOption={false}
                                placeholder="请输入设备名称或序列号"
                                value={deviceId}
                                options={devices.map(device => ({
                                    value: device.id,
                                    label: `${device.name || DEFAULT_VALUE} | ${device.serialNo || DEFAULT_VALUE}`
                                }))}
                                onSearch={searchDevices}
                                onChange={value => {
                                    setDeviceId(value);
                                    setDeviceError("");
                                }}
                            />
                        </Form.Item>
                        <Form.Item
                            label="安装位置"
                            validateStatus={positionError ? "error" : undefined}
                            help={positionError || undefined}
                        >
                            <Input
                                aria-label="安装位置"
                                maxLength={20}
                                placeholder="请输入安装位置"
                                value={position}
                                onChange={event => {
                                    setPosition(event.target.value);
                                    setPositionError("");
                                }}
                            />
                        </Form.Item>
                    </ZhGrid>
                    <Space>
                        {canBind && (claim === undefined || Number(claim.state) === CLAIM_STATE.CLAIMING.code) ? (
                            <Button aria-label="提交" disabled={isButtonBanned} htmlType="submit" type="primary">
                                提交
                            </Button>
                        ) : null}
                        <Button onClick={() => navigate(-1)}>返回</Button>
                    </Space>
                </Form>
            </Card>
        </section>
    );
}
